import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import styles from "../../styles/shap_of_you.module.scss";

interface LiveCameraPermissionModalProps {
  show: boolean;
  onHide: () => void;
  onPermissionGranted: () => void;
  onPermissionDenied: () => void;
  logStep?: (eventName: string, payload?: any) => void;
}

const LiveCameraPermissionModal: React.FC<LiveCameraPermissionModalProps> = ({
  show,
  onHide,
  onPermissionGranted,
  onPermissionDenied,
  logStep,
}) => {
  const [isRequesting, setIsRequesting] = useState(false);

  // Handle "Allow this time" - request camera permission
  const handleAllowThisTime = async () => {
    setIsRequesting(true);
    try {
      // Request camera permission from browser
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });

      // Permission granted! Stop the stream and save to localStorage
      stream.getTracks().forEach((track) => track.stop());
      localStorage.setItem("cameraPermissionGranted", "true");

      // Log camera permission granted (non-blocking)
      if (logStep) {
        logStep("camera_permission_granted");
      }

      // Close modal and trigger success callback
      onHide();
      onPermissionGranted();
    } catch (error) {
      console.error("Camera permission denied:", error);

      // Log camera permission denied (non-blocking)
      if (logStep) {
        logStep("camera_permission_denied", {
          error_message: "User denied camera permission",
        });
      }

      // Close modal and trigger denied callback
      onHide();
      onPermissionDenied();
    } finally {
      setIsRequesting(false);
    }
  };

  // Handle "Allow on every visit" - same as "Allow this time" for camera
  const handleAllowEveryVisit = async () => {
    await handleAllowThisTime();
  };

  // Handle "Don't Allow"
  const handleDontAllow = () => {
    // Log camera permission denied (non-blocking)
    if (logStep) {
      logStep("camera_permission_denied", {
        error_message: "User clicked Don't Allow",
      });
    }

    // Close modal
    onHide();

    // Trigger denied callback
    onPermissionDenied();
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} className={styles.custom_modal} centered>
      <Modal.Body>
        <div className={styles.modal}>
          <h3>
            &ldquo;sencogoldanddiamonds.com&rdquo; <br /> wants to
          </h3>
          <label className={styles.checkbox}>
            <svg
              width="20"
              height="16"
              viewBox="0 0 30 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 24C2.175 24 1.46875 23.7063 0.88125 23.1188C0.29375 22.5312 0 21.825 0 21V3C0 2.175 0.29375 1.46875 0.88125 0.88125C1.46875 0.29375 2.175 0 3 0H21C21.825 0 22.5312 0.29375 23.1188 0.88125C23.7063 1.46875 24 2.175 24 3V9.75L30 3.75V20.25L24 14.25V21C24 21.825 23.7063 22.5312 23.1188 23.1188C22.5312 23.7063 21.825 24 21 24H3ZM3 21H21V3H3V21Z"
                fill="#46474C"
              />
            </svg>
            <span>Use your camera</span>
          </label>
          <div className={styles.actions}>
            <button
              className={styles.primary}
              onClick={handleAllowThisTime}
              disabled={isRequesting}
            >
              {isRequesting ? "Requesting..." : "Allow this time"}
            </button>
            <button
              className={styles.primary}
              onClick={handleAllowEveryVisit}
              disabled={isRequesting}
            >
              Allow on every visit
            </button>
            <button
              className={styles.primary}
              onClick={handleDontAllow}
              disabled={isRequesting}
            >
              Don&apos;t Allow
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LiveCameraPermissionModal;

/*
  ============================================
  PREVIOUS FIXED OVERLAY DESIGN (COMMENTED OUT)
  ============================================
  This is the old full-screen fixed overlay design for the livestreaming modal.
  Kept for future reference. The new design uses React-Bootstrap Modal to match
  the desktop CameraPermissionScreen layout.
  
  Old JSX:
  
  return (
    <div
      className="d-none d-md-flex"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "20px",
          padding: "20px",
          textAlign: "left",
          maxWidth: "420px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
        }}
      >
        <h3
          style={{
            fontSize: "20px",
            fontWeight: 400,
            marginBottom: "8px",
            color: "#000",
            fontFamily: "Sweet Sans Pro Regular",
          }}
        >
          &ldquo;Senco Gold&rdquo; Would Like To Access Your Camera
        </h3>
        <p
          style={{
            fontSize: "14px",
            color: "#666",
            marginBottom: "20px",
            lineHeight: 1.4,
          }}
        >
          This app requires access to camera
        </p>

        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button
            onClick={() => {
              handleDontAllow();
            }}
            disabled={isRequesting}
            style={{
              flex: 1,
              padding: "10px 12px",
              backgroundColor: "#f0f0f0",
              border: "none",
              borderRadius: "20px",
              fontSize: "15px",
              fontWeight: 500,
              color: "#333",
              cursor: isRequesting ? "not-allowed" : "pointer",
            }}
          >
            Don&apos;t Allow
          </button>
          <button
            onClick={handleAllowThisTime}
            disabled={isRequesting}
            style={{
              flex: 1,
              padding: "10px 12px",
              backgroundColor: "#007AFF",
              border: "none",
              borderRadius: "20px",
              fontSize: "15px",
              fontWeight: 500,
              color: "white",
              cursor: isRequesting ? "not-allowed" : "pointer",
            }}
          >
            {isRequesting ? "Requesting..." : "OK"}
          </button>
        </div>
      </div>
    </div>
  );
*/
